import { type QueryClient } from "@tanstack/react-query";

import { isAskPunttEnabledForEnterprise, isDanoneBetaTester } from "./auth";

import { getGuidelines } from "../services/brand-guidelines";
import { getEnterprises } from "../services/enterprises";

/**
 * Single source of truth for the default landing target after
 * authentication when no explicit redirect is set. Landing follows the
 * enterprise Ask Puntt toggle (and Danone beta), NOT the broader
 * `canAccessAskPuntt()` gate — MG staff can open Ask Puntt when the
 * toggle is on, but enterprises with the toggle off (including
 * Meaningful Gigs) land on Documents (`/tickets`).
 *
 * Sites with MG-specific login routing (`LoginView.tsx`) preserve the
 * existing divergence where already-authed MG users on `/login` go to
 * `/roi` instead of this helper.
 *
 * Callers MUST honor any explicit redirect (share-link `redirectTo`,
 * login `?redirect=`) BEFORE consulting this helper. The helper only
 * answers "where does a freshly-authed user go when no explicit target
 * is set?"
 *
 * The synchronous variant is safe ONLY from sites where the
 * `brand-guidelines` and `enterprises` query caches are guaranteed
 * primed — routes under `authLayoutRoute`, whose `beforeLoad` awaits
 * both caches before any child `beforeLoad` runs (see
 * `routes/auth-layout/route.tsx`). Priming in the `loader` is too late
 * for child `beforeLoad`s.
 *
 * Outside that layout, use `ensureDefaultPostAuthLandingPath()`.
 *
 * Path strings are inlined rather than imported from the route objects
 * to avoid a TDZ-cycle (rootRoute → unauthLayout → this module →
 * askPunttRoute → authLayoutRoute → rootRoute). See
 * `routes/vitalora-home/view.tsx:187-200` for the established
 * precedent in this codebase.
 */
export const ASK_PUNTT_PATH = "/ask-puntt";
export const TICKETS_PATH = "/tickets";

export type DefaultPostAuthLanding = "ask-puntt" | "tickets";

/** True when the default landing should be Ask Puntt (not Documents). */
export function defaultLandingUsesAskPuntt(): boolean {
  return isDanoneBetaTester() || isAskPunttEnabledForEnterprise();
}

export function defaultPostAuthLandingPath(): string {
  return defaultLandingUsesAskPuntt() ? ASK_PUNTT_PATH : TICKETS_PATH;
}

export function defaultPostAuthLandingKind(): DefaultPostAuthLanding {
  return defaultLandingUsesAskPuntt() ? "ask-puntt" : "tickets";
}

/**
 * Async variant for call sites outside the auth layout. Primes both
 * caches before resolving the path. Silently degrades on fetch failure
 * (cache miss → toggle reads false → resolves to `/tickets`), matching
 * the auth-layout loader's own error-swallowing behavior.
 */
export async function ensureDefaultPostAuthLandingPath(
  queryClient: QueryClient,
): Promise<string> {
  await Promise.all([
    queryClient
      .ensureQueryData({ queryKey: ["enterprises"], queryFn: getEnterprises })
      .catch(() => undefined),
    queryClient
      .ensureQueryData({
        queryKey: ["brand-guidelines"],
        queryFn: getGuidelines,
      })
      .catch(() => undefined),
  ]);
  return defaultPostAuthLandingPath();
}
